// Permit2 (Uniswap) SignatureTransfer helpers for the frontend (INTENT-UX-PLAN.md Phase 2). A user
// approves this singleton ONCE per token, then every action carries a signed, single-use permit
// instead of a fresh approve tx. useAction uses this on wallets that can't do EIP-5792 atomic
// batching, to still collapse approve+action into one on-chain transaction.

// Canonical Permit2 - the same address on every chain (deployed via CREATE2). On the local dev chain
// MockPermit2's code is etched here (see scripts/deployLocal.cjs), so the flow is identical.
export const PERMIT2_ADDRESS = '0x000000000022D473030F116dDEE9F6B43aC78BA3'

// EIP-712 types for a single-token SignatureTransfer permit. `spender` is bound into the signature
// (Permit2 checks it equals the calling contract), but is NOT part of the struct passed on-chain -
// there the spender is msg.sender.
export const PERMIT2_TRANSFER_TYPES = {
  PermitTransferFrom: [
    { name: 'permitted', type: 'TokenPermissions' },
    { name: 'spender', type: 'address' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
  ],
  TokenPermissions: [
    { name: 'token', type: 'address' },
    { name: 'amount', type: 'uint256' },
  ],
}

/// A 256-bit random nonce. Permit2 tracks used nonces in an unordered bitmap, so a random value is
/// single-use with negligible collision risk (no per-owner sequencing needed).
export function randomPermitNonce() {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  let hex = '0x'
  for (const b of bytes) hex += b.toString(16).padStart(2, '0')
  return BigInt(hex)
}

/// Sign a SignatureTransfer permit. Returns `{ permit, signature }` where `permit` is exactly the
/// on-chain `PermitTransferFrom` tuple ({ permitted, nonce, deadline }) the *WithPermit entrypoints
/// expect - `spender` lives only in the signature.
export async function signPermitTransfer(walletClient, { owner, token, amount, spender, chainId, deadlineSecs = 1800 }) {
  const nonce = randomPermitNonce()
  const deadline = BigInt(Math.floor(Date.now() / 1000) + deadlineSecs)
  const signature = await walletClient.signTypedData({
    account: owner,
    domain: { name: 'Permit2', chainId, verifyingContract: PERMIT2_ADDRESS },
    types: PERMIT2_TRANSFER_TYPES,
    primaryType: 'PermitTransferFrom',
    message: { permitted: { token, amount }, spender, nonce, deadline },
  })
  return { permit: { permitted: { token, amount }, nonce, deadline }, signature }
}
